import { get, uniqBy } from 'lodash'
import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { GenerateDocumentModel } from '../../../../../models/epic/GenerateDocument.model'
import CertificateListService from '../../../../../services/CertificateListService'
import EpicIntegrationService from '../../../../../services/EpicIntegrationService'
import { DocumentCategoryModel } from '../../../../../shared/models/DocumentCategory.model'
import { downloadFile } from '../../../../../utils/helpers/file.helpers'
import useToastr from '../../../../../hooks/useToastr'
import moment from 'moment'
import { DocumentType } from '../../../../../enums/DocumentType'

export const documentTypeOptions: { label: string; value: DocumentType }[] = [
  {
    label: 'Default (docx.)',
    value: DocumentType.DEFAULT,
  },
  {
    label: 'HTML',
    value: DocumentType.HTML,
  },
  {
    label: 'PDF',
    value: DocumentType.PDF,
  },
  {
    label: 'Email',
    value: DocumentType.EMAIL,
  },
]

const useGenerateDocument = (certificate: {
  certificateId: string
  certificateType: string
  certificateName: string
  ownerReference: string
} | null | undefined) => {
  const { showSuccess, showError } = useToastr()
  const certificateData = certificate ?? {
    certificateId: '',
    certificateType: '',
    certificateName: '',
    ownerReference: '',
  }
  const { certificateType, certificateId } = certificateData
  const [visible, setVisible] = useState<boolean>(false)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>()
  const [searchPhrase, setSearchPhrase] = useState<string>('')
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType>()
  const [documentName, setDocumentName] = useState<string>('')
  const [isGeneratingDocument, setIsGeneratingDocument] = useState<boolean>(false)
  const documentsQuery = useQuery(
    [certificateType, 'certificate-documents'],
    async () => {
      const result =
        await CertificateListService.getCertificateDocumentsByCertificateScheme(certificateType)
      return uniqBy(
        (result.data as DocumentCategoryModel[] | null)?.filter(dc => dc.documentList.length > 0),
        dc => dc.documentList[0].externalDocumentId
      )
    },
    {
      enabled: !!certificateType && visible,
      refetchOnWindowFocus: false,
      onSuccess: data => {
        if (data.length === 1 && data[0].documentList[0]?.externalDocumentId) {
          setSelectedTemplateId(data[0].documentList[0].externalDocumentId)
        }
      },
    }
  )
  const isNoDocumentsFound = !documentsQuery.isFetching && documentsQuery.data?.length === 0
  const templateInfoQuery = useQuery(
    [selectedTemplateId, 'template-info'],
    async () => {
      if (!selectedTemplateId) return null
      const result = await EpicIntegrationService.getTemplateInfo(selectedTemplateId)
      return result.data
    },
    {
      enabled: visible && !!selectedTemplateId,
      refetchOnWindowFocus: false,
      onSettled: () => {
        setSelectedDocumentType(DocumentType.DEFAULT)
        setDocumentName('')
      },
    }
  )
  const filteredDocuments = documentsQuery.data?.filter(({ documentCategory }) =>
    documentCategory.toLowerCase().includes(searchPhrase.toLowerCase())
  )
  const canGenerate = !!selectedTemplateId && !templateInfoQuery.isFetching

  useEffect(() => {
    setSelectedTemplateId(undefined)
    setSearchPhrase('')
    setSelectedDocumentType(undefined)
    setIsGeneratingDocument(false)
    setDocumentName('')
  }, [visible])

  const constsructTemplateParameters = () => {
    if (!templateInfoQuery.data || templateInfoQuery.data.length === 0) return {}
    const templateInfo = templateInfoQuery.data[0]!
    const parameters: { [key: string]: unknown } = {}
    templateInfo.variables.forEach(variable => {
      let value = variable.defaultValue

      if (!value && get(certificateData, variable.variableName)) {
        value = get(certificateData, variable.variableName)
      }

      parameters[variable.variableName] = value
    })
    return parameters
  }

  const selectedDocument = documentsQuery.data?.find(
    dc => dc.documentList[0]?.externalDocumentId === selectedTemplateId
  )

  const generateDocument = async () => {
    try {
      setIsGeneratingDocument(true)
      const model: GenerateDocumentModel = {
        certificateId,
        templateId: selectedTemplateId!,
        templateName: selectedDocument!.documentCategory,
        documentName,
        documentType: selectedDocumentType || DocumentType.DEFAULT,
        metadata: {},
        nodeId: '',
        returnDeliveryType: 'Download',
        templateParameters: constsructTemplateParameters(),
      }
      const result = await EpicIntegrationService.downloadDocument(model)
      downloadFile(result)
      showSuccess('Download complete!', 'Your document has been successfully downloaded.')
    } catch (error) {
      showError('Document generation failed.', 'Failed to generate document. Please try again.')
    } finally {
      setIsGeneratingDocument(false)
    }
  }

  const hide = () => {
    setVisible(false)
  }

  const show = () => {
    setVisible(true)
  }

  return {
    hide,
    show,
    visible,
    documentsQuery,
    selectedTemplateId,
    setSelectedTemplateId,
    filteredDocuments,
    searchPhrase,
    setSearchPhrase,
    templateInfoQuery,
    selectedDocumentType,
    setSelectedDocumentType,
    isNoDocumentsFound,
    canGenerate,
    generateDocument,
    isGeneratingDocument,
    documentName,
    setDocumentName,
  }
}

export type UseGenerateDocumentModalReturnType = ReturnType<typeof useGenerateDocument>

export default useGenerateDocument
